import {
  ArrowUpIcon,
  BodyText,
  Card,
  Flex_unstable as Flex,
  Heading,
  IconButton,
  List,
} from '@farol-ds/react'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { useDispatcher } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef, useState } from 'react'

import { buildWhatsappRedirect } from '../../utils/whatsapp-redirect'
import styles from './chat-web.module.scss'
import { SparklesAnimation } from './sparkles-animation'

export interface ChatWebProps {
  title?: string
  items: string[]
  placeholder?: string
  docId?: number | string
}

const PAGE = 'docview-artigo'
const APP_ID = 'web-docview'
const ACTION = 'send_question'
const EXPERIMENT = 'sd-docview-chats'
const VARIANT = 'variantA'

export const DECORATIVE_TEXT_VARIANTS = [
  'Pergunte o que quiser. A gente entende.',
  'Quais são meus direitos nessa situação?',
  'Como isso se aplica ao meu caso?',
  'Consegue resumir pra mim?',
] as const

export const ChatWeb = ({
  title = 'Resumo',
  items,
  placeholder = 'Faça sua pergunta',
  docId,
}: ChatWebProps) => {
  const [question, setQuestion] = useState('')
  const [isAnchored, setIsAnchored] = useState(false)
  const [decorativeText, setDecorativeText] = useState<string>(DECORATIVE_TEXT_VARIANTS[0])
  const session = useUserSession()
  const slotRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLButtonElement>(null)
  const topViewSent = useRef(false)
  const bottomViewSent = useRef(false)

  const { ref: dispatcherRef, sendMetrics } = useDispatcher<HTMLDivElement>()
  const sendMetricsRef = useRef(sendMetrics)
  sendMetricsRef.current = sendMetrics

  const isCtaOnScreen = useOnScreen(ctaRef, { threshold: 1 }, false)

  const userId = session?.pid != null ? String(session.pid) : null

  const metadata = useMemo(
    () => ({
      page: PAGE,
      action: ACTION,
      position: isAnchored ? 'sticky' : 'top',
      doc_id: docId ?? null,
      experiment: EXPERIMENT,
      variant: VARIANT,
      cta: isAnchored ? decorativeText : 'Faça sua pergunta',
    }),
    [isAnchored, docId, decorativeText]
  )

  useEffect(() => {
    const index = Math.floor(Math.random() * DECORATIVE_TEXT_VARIANTS.length)
    setDecorativeText(DECORATIVE_TEXT_VARIANTS[index])
  }, [])

  useEffect(() => {
    const target = slotRef.current
    if (!target || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0
        setIsAnchored(entry.intersectionRatio < 0.5 && scrolledPast)
      },
      { threshold: [0, 0.5, 1] }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isCtaOnScreen && !isAnchored && !topViewSent.current) {
      sendMetricsRef.current('SeusDireitos.CtaView', userId, {
        app_id: APP_ID,
        page: PAGE,
        metadata: { ...metadata, position: 'top' },
      })
      topViewSent.current = true
    }

    if (isAnchored && !bottomViewSent.current) {
      sendMetricsRef.current('SeusDireitos.CtaView', userId, {
        app_id: APP_ID,
        page: PAGE,
        metadata: { ...metadata, position: 'sticky' },
      })
      bottomViewSent.current = true
    }
  }, [isCtaOnScreen, isAnchored, metadata, userId])

  const handleSend = () => {
    sendMetrics('SeusDireitos.CtaClick', userId, {
      app_id: APP_ID,
      page: PAGE,
      metadata,
    })

    const trimmed = question.trim()
    if (!trimmed) return

    sendMetrics('SeusDireitos.QuestionSent', userId, {
      app_id: APP_ID,
      page: PAGE,
      metadata: { ...metadata, question: trimmed },
    })

    const url = buildWhatsappRedirect({
      message: trimmed,
      deviceId: session?.jdid,
      userId: session?.pid != null ? String(session.pid) : undefined,
    })
    window.open(url, '_blank', 'noopener,noreferrer')
    setQuestion('')
  }

  return (
    <Card ref={dispatcherRef}>
      <Flex direction="column" gap="5xs">
        <Flex direction="column" gap="6xs">
          <Heading size="sm">{title}</Heading>
          <List unstyled={false}>
            {items.map((item, index) => (
              <List.Item key={index}>
                <BodyText>{item}</BodyText>
              </List.Item>
            ))}
          </List>
        </Flex>

        <div ref={slotRef} className={styles.chatBarSlot}>
          <div className={styles.chatBarWrapper} data-anchored={isAnchored}>
            <Flex align="center" gap="5xs" className={styles.decorativeBar} aria-hidden="true">
              <SparklesAnimation />
              <BodyText size="md" className={styles.decorativeText}>
                {decorativeText}
              </BodyText>
            </Flex>

            <div className={styles.chatFieldArea}>
              <Flex align="center" gap="5xs" className={styles.chatField}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={placeholder}
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  aria-label={placeholder}
                />
                <IconButton
                  ref={ctaRef}
                  onClick={handleSend}
                  kind="tertiary"
                  size="md"
                  icon={<ArrowUpIcon />}
                  label="Enviar pergunta"
                />
              </Flex>
            </div>
          </div>
        </div>
      </Flex>
    </Card>
  )
}
